/**
 * Form controls bound to a path in the document: each reads its value from the document and writes
 * one path back. While the YAML does not parse they are read-only (the YAML view is where to fix it).
 */
import { useId, type ReactNode } from "react";
import { FieldIssues, useIssuesAt } from "./issues";
import type { Path } from "./specDocument";
import type { Builder } from "./useBuilder";

interface Bound {
  builder: Builder;
  path: Path;
  label: string;
  hint?: string;
}

const INPUT =
  "w-full rounded-md border border-[var(--color-border)] bg-transparent px-3 py-1.5 disabled:opacity-60 aria-[invalid=true]:border-[var(--color-danger)]";

/** What a bound control needs for its issues: aria-invalid, aria-describedby, and its path. */
function useFieldA11y(id: string, path: Path, hint?: string) {
  const issues = useIssuesAt(path);
  const described = [hint ? `${id}-hint` : "", issues.length ? `${id}-issues` : ""].filter(Boolean).join(" ");
  return {
    issues,
    attrs: {
      "aria-invalid": issues.some((i) => i.severity === "error") || undefined,
      "aria-describedby": described || undefined,
      "data-path": path.join("."),
    },
  };
}

function Frame({ id, label, hint, issues = [], children }: {
  id: string;
  label: string;
  hint?: string;
  issues?: ReturnType<typeof useIssuesAt>;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block text-sm font-medium">
        {label}
      </label>
      {children}
      {hint && (
        <p id={`${id}-hint`} className="text-xs text-[var(--color-muted)]">
          {hint}
        </p>
      )}
      <FieldIssues id={`${id}-issues`} issues={issues} />
    </div>
  );
}

const locked = (builder: Builder) => builder.problems.length > 0;
const asString = (value: unknown) => (value === undefined || value === null ? "" : String(value));

export function TextField({ builder, path, label, hint, multiline, mono, placeholder, required, rows = 6 }: Bound & {
  /** Emptying it keeps the field (the spec needs it) instead of removing it. */
  required?: boolean;
  multiline?: boolean;
  mono?: boolean;
  placeholder?: string;
  rows?: number;
}) {
  const id = useId();
  const { issues, attrs } = useFieldA11y(id, path, hint);
  const common = {
    id,
    value: asString(builder.doc.get(path)),
    disabled: locked(builder),
    placeholder,
    ...attrs,
    "aria-required": required || undefined,
    className: `${INPUT} ${mono ? "font-mono text-sm" : ""}`,
    spellCheck: !mono,
  };
  return (
    <Frame id={id} label={label} hint={hint} issues={issues}>
      {multiline ? (
        <textarea {...common} rows={rows} onChange={(e) => builder.setPath(path, e.target.value, { keepEmpty: required })} />
      ) : (
        <input {...common} onChange={(e) => builder.setPath(path, e.target.value, { keepEmpty: required })} />
      )}
    </Frame>
  );
}

export function NumberField({ builder, path, label, hint, min, max, step = 1 }: Bound & {
  min?: number;
  max?: number;
  step?: number;
}) {
  const id = useId();
  const { issues, attrs } = useFieldA11y(id, path, hint);
  return (
    <Frame id={id} label={label} hint={hint} issues={issues}>
      <input
        id={id}
        type="number"
        inputMode="decimal"
        min={min}
        max={max}
        step={step}
        value={asString(builder.doc.get(path))}
        disabled={locked(builder)}
        {...attrs}
        className={`${INPUT} max-w-40`}
        onChange={(e) => builder.setPath(path, e.target.value === "" ? undefined : Number(e.target.value))}
      />
    </Frame>
  );
}

export function Toggle({ builder, path, label, hint, defaultValue = false }: Bound & { defaultValue?: boolean }) {
  const id = useId();
  const value = builder.doc.get(path);
  return (
    <div className="flex items-start gap-3">
      <input
        id={id}
        type="checkbox"
        role="switch"
        checked={typeof value === "boolean" ? value : defaultValue}
        disabled={locked(builder)}
        aria-describedby={hint ? `${id}-hint` : undefined}
        className="mt-1 size-4 accent-[var(--color-primary)]"
        onChange={(e) => builder.setPath(path, e.target.checked)}
      />
      <div>
        <label htmlFor={id} className="text-sm font-medium">
          {label}
        </label>
        {hint && (
          <p id={`${id}-hint`} className="text-xs text-[var(--color-muted)]">
            {hint}
          </p>
        )}
      </div>
    </div>
  );
}

export interface Choice {
  value: string;
  label: string;
}

export function SelectField({ builder, path, label, hint, choices, empty }: Bound & {
  choices: Choice[];
  /** The label of "not set" (the API's default applies); without it, a value is required. */
  empty?: string;
}) {
  const id = useId();
  const { issues, attrs } = useFieldA11y(id, path, hint);
  const value = asString(builder.doc.get(path));
  // a value the list does not know (typed in YAML, or not loaded yet) is kept and shown as it is
  const all = value && !choices.some((c) => c.value === value) ? [...choices, { value, label: value }] : choices;
  return (
    <Frame id={id} label={label} hint={hint} issues={issues}>
      <select
        id={id}
        value={value}
        disabled={locked(builder)}
        {...attrs}
        className={`${INPUT} max-w-sm`}
        onChange={(e) => builder.setPath(path, e.target.value)}
      >
        {(empty !== undefined || !value) && <option value="">{empty ?? ""}</option>}
        {all.map((c) => (
          <option key={c.value} value={c.value}>
            {c.label}
          </option>
        ))}
      </select>
    </Frame>
  );
}
